import { useMutation, useMutationState, useQuery, useQueryClient, type MutationState } from "@tanstack/react-query";
import { usePermissions } from "@/hooks/use-permissions";
import { ApiError, apiRequest } from "@/lib/query-client";

/**
 * The QA test drive's Run agent hooks (Agents A–I). Every read goes through the Run route's status route first: it is
 * mounted only outside production with ENABLE_AGENT_QA_RUN on and answers admins only, so 404/403 → no data →
 * every button, panel and page here stays hidden. Agents only draft and suggest; nothing here sends.
 */

export const AGENT_QA_STATUS_KEY = ["/api/agents/qa-run/status"] as const;
export const AGENT_QA_RUNS_KEY = ["/api/agents/qa-run/runs"] as const;
export const AGENT_QA_EMAILS_KEY = ["/api/agents/qa-run/emails"] as const;
export const AGENT_QA_REVIEW_KEY = ["/api/agents/qa-run/review"] as const;
export const AGENT_QA_SCOREBOARD_KEY = ["/api/agents/qa-run/scoreboard"] as const;
/** The emails route's own cap (limit 1 to 50). */
export const AGENT_QA_EMAILS_MAX = 50;

export const NOTHING_IS_SENT = "Nothing is sent: agents only draft and suggest.";

export const AGENTS_TEST_DRIVE_PATH = "/agents/test-drive";
export const AGENTS_REVIEW_PATH = "/agents/review";
/** Where "See what it proposed" goes: the review screen, filtered to the run. */
export const agentRunResultsHref = (runId: string) => `${AGENTS_REVIEW_PATH}?runId=${encodeURIComponent(runId)}`;

/** The firm's time zone (the firm settings' default); run times show in it with a "CT" label. */
export const FIRM_TIME_ZONE = "America/Chicago";
const FIRM_TIME_FORMAT = new Intl.DateTimeFormat("en-US", {
  timeZone: FIRM_TIME_ZONE,
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

export function formatFirmTime(iso: string | null | undefined): string {
  if (!iso) return "No time";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "No time" : `${FIRM_TIME_FORMAT.format(date)} CT`;
}

export type AgentScope = "claim" | "email" | "firm";

/** One agent as the status route tells the buttons about it, for the session's firm. */
export interface AgentQaState {
  agent: string;
  letter: string | null;
  /** The only place an agent's name comes from: the client keeps no copy. */
  label: string;
  globalSwitch: { name: string; on: boolean } | null;
  mode: string;
  killed: boolean;
  runnable: boolean;
  /** Runnable, and a person would see what it writes (pilot or on). Shadow writes nothing a person sees. */
  visible: boolean;
  scopes: AgentScope[];
  triggers: { claim?: string[]; firm?: string[] };
}

export interface AgentQaStatus {
  enabled: boolean;
  agents: string[];
  modelMode: string;
  agentStates?: AgentQaState[];
}

export interface AgentQaRunCounts {
  drafts: number;
  tasks: number;
  notes: number;
  flags: number;
}

export interface AgentQaRun {
  id: string;
  agent: string;
  letter: string | null;
  label: string;
  mode: string | null;
  status: string;
  claimId: string | null;
  claimNumber: string | null;
  startedAt: string | null;
  summary: string | null;
  counts: AgentQaRunCounts;
}

export interface AgentQaProposal {
  id: string;
  runId: string;
  agent: string;
  letter: string | null;
  claimId: string | null;
  claimNumber: string | null;
  type: "draft" | "task" | "timeline_note" | "flag";
  title: string;
  detail: string | null;
  assignee: { userId: string; name: string | null; email: string | null } | null;
  role: string | null;
  reason: string;
  status: string;
  createdAt: string | null;
}

export interface AgentQaEmail {
  id: string;
  subject: string | null;
  sender: string;
  receivedAt: string | null;
  /** The matched claim when it is the firm's; null otherwise. */
  matchedClaimId: string | null;
  claimNumber: string | null;
}

/** What one run, claim, processed email or the firm, is about. */
export type AgentRunItem =
  | { scope: "claim"; claimId: string }
  | { scope: "email"; processedEmailId: string; matchedClaimId: string | null }
  | { scope: "firm"; trigger: "timer" }
  | { scope: "firm"; trigger: "license_application"; application: { state: string; businessName: string } };

export interface AgentRunResult {
  agent: string;
  claimId: string | null;
  mode: string;
  runId: string | null;
  status: string;
  triage: string | null;
  draftId: string | null;
}

/** Agents B, E, F and G work on a claim: on a processed email only when it is matched to one (the Run route's rule). */
const EMAIL_NEEDS_CLAIM: ReadonlySet<string> = new Set(["representation", "appraisal", "checks", "invoicing"]);
/** The claim page's email buttons: A, B, D, E, F and G. H and I work for the firm, on the test-drive page. */
export const CLAIM_PAGE_EMAIL_AGENTS: ReadonlySet<string> = new Set([
  "intake",
  "representation",
  "carrier_response",
  "appraisal",
  "checks",
  "invoicing",
]);

/** Whether a run of this agent on this item would start: runnable for the firm, and the item's scope is one it takes. */
export function canRunOn(state: AgentQaState | undefined, item: AgentRunItem): state is AgentQaState {
  if (!state?.runnable || !state.scopes.includes(item.scope)) return false;
  if (item.scope === "email") return !!item.matchedClaimId || !EMAIL_NEEDS_CLAIM.has(state.agent);
  if (item.scope === "firm") return (state.triggers.firm ?? []).includes(item.trigger);
  return true;
}

/** The triggers a person picks from for this item: B, C and F on a claim. The firm scope's trigger is the item's own. */
export function itemTriggers(state: AgentQaState, item: AgentRunItem): string[] {
  return item.scope === "claim" ? state.triggers.claim ?? [] : [];
}

function itemKey(item: AgentRunItem): string {
  if (item.scope === "claim") return `claim:${item.claimId}`;
  if (item.scope === "email") return `email:${item.processedEmailId}`;
  return `firm:${item.trigger}`;
}

function runBody(item: AgentRunItem, trigger: string | undefined): Record<string, unknown> {
  if (item.scope === "claim") return trigger ? { claimId: item.claimId, trigger } : { claimId: item.claimId };
  if (item.scope === "email") return { processedEmailId: item.processedEmailId };
  if (item.trigger === "license_application") return { scope: "firm", trigger: item.trigger, application: item.application };
  return { scope: "firm", trigger: "timer" };
}

/** The status route, for admins outside production only. No data (404, 403, not an admin) → nothing shows. */
export function useAgentQaStatus() {
  const perms = usePermissions();
  const query = useQuery<AgentQaStatus>({
    queryKey: AGENT_QA_STATUS_KEY,
    enabled: !perms.isLoading && !!perms.isAdmin && import.meta.env.VITE_APP_ENV !== "production",
    retry: false,
  });
  const enabled = query.data?.enabled === true;
  return { query, enabled, states: enabled ? query.data?.agentStates ?? [] : [] };
}

/** The firm's runs, newest first; with claimId, that claim's only. */
export function useAgentRuns({ claimId, limit, enabled = true }: { claimId?: string; limit?: number; enabled?: boolean } = {}) {
  const status = useAgentQaStatus();
  return useQuery<{ runs: AgentQaRun[] }>({
    queryKey: [...AGENT_QA_RUNS_KEY, { ...(claimId ? { claimId } : {}), ...(limit ? { limit } : {}) }],
    enabled: status.enabled && enabled,
    retry: false,
  });
}

export type AgentQaDecisionKind = "approve" | "edit" | "reject";

/** A proposal's current decision: its newest. */
export interface AgentQaDecision {
  decision: AgentQaDecisionKind;
  by: { userId: string; name: string | null } | null;
  at: string | null;
}

/** One proposal on the review screen: the run's proposal, its agent's label and its current decision. */
export interface AgentQaReviewItem extends AgentQaProposal {
  label: string;
  entity: { type: "email_draft" | "task" | "timeline" | null; id: string | null };
  decision: AgentQaDecision | null;
  /** What Edit starts from (the full current values); null for a note, a flag or a row that is gone. */
  editable:
    | { subject: string; body: string | null }
    | { title: string; description: string | null; assigneeUserId: string | null }
    | null;
}

export interface AgentQaReviewFilters {
  agent?: string;
  claimId?: string;
  runId?: string;
  state?: "open" | "decided";
  days?: number;
}

export interface AgentQaScoreWindow {
  approved: number;
  edited: number;
  rejected: number;
  total: number;
  rate: number | null;
  approvedOrEdited: number | null;
}

export interface AgentQaScoreRow {
  agent: string;
  letter: string | null;
  label: string;
  d7: AgentQaScoreWindow;
  d30: AgentQaScoreWindow;
}

export interface AgentQaDecisionBody {
  decision: AgentQaDecisionKind;
  edit?: { subject?: string; body?: string; title?: string; description?: string; assigneeUserId?: string };
  comment?: string;
}

export interface AgentQaDecisionResult {
  proposalId: string;
  proposalType: AgentQaProposal["type"];
  actionId: string;
  decision: AgentQaDecision;
  status: string;
  changed: boolean;
}

/** The firm's proposals across its runs, newest first (at most 100), with each one's current decision. */
export function useAgentReview(filters: AgentQaReviewFilters) {
  const status = useAgentQaStatus();
  const query = Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined && value !== ""));
  return useQuery<{ proposals: AgentQaReviewItem[] }>({
    queryKey: [...AGENT_QA_REVIEW_KEY, query],
    enabled: status.enabled,
    retry: false,
  });
}

/** Each agent's approved, edited and rejected counts over 7 and 30 days. */
export function useAgentScoreboard() {
  const status = useAgentQaStatus();
  return useQuery<{ now: string; agents: AgentQaScoreRow[] }>({
    queryKey: AGENT_QA_SCOREBOARD_KEY,
    enabled: status.enabled,
    retry: false,
  });
}

/** Records Approve, Edit or Reject on one proposal; then the list, the scoreboard, the runs and the tasks are read again. */
export function useDecideProposal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ proposalId, body }: { proposalId: string; body: AgentQaDecisionBody }): Promise<AgentQaDecisionResult> => {
      const res = await apiRequest("POST", `/api/agents/qa-run/proposals/${encodeURIComponent(proposalId)}/decision`, body);
      return res.json();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_QA_REVIEW_KEY });
      queryClient.invalidateQueries({ queryKey: AGENT_QA_SCOREBOARD_KEY });
      queryClient.invalidateQueries({ queryKey: AGENT_QA_RUNS_KEY });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
    },
  });
}

/** The firm's processed emails, newest first. */
export function useAgentQaEmails({ limit, enabled = true }: { limit?: number; enabled?: boolean } = {}) {
  const status = useAgentQaStatus();
  return useQuery<{ emails: AgentQaEmail[] }>({
    queryKey: [...AGENT_QA_EMAILS_KEY, limit ? { limit } : {}],
    enabled: status.enabled && enabled,
    retry: false,
  });
}

export type AgentRunState = MutationState<AgentRunResult, Error, string | undefined, unknown>;

/**
 * Runs one agent on one item. The mutation key is per agent and item, and the latest run is read from the mutation
 * cache, so a list or menu that unmounts and mounts again keeps the pending state and the result.
 */
export function useRunAgent(agent: string, item: AgentRunItem) {
  const queryClient = useQueryClient();
  const mutationKey = ["agent-qa-run", agent, itemKey(item)];
  const mutation = useMutation({
    mutationKey,
    mutationFn: async (trigger?: string): Promise<AgentRunResult> => {
      const res = await apiRequest("POST", `/api/agents/${encodeURIComponent(agent)}/run`, runBody(item, trigger));
      return res.json();
    },
    onSettled: (result) => {
      queryClient.invalidateQueries({ queryKey: AGENT_QA_RUNS_KEY });
      const claimId = result?.claimId ?? (item.scope === "claim" ? item.claimId : item.scope === "email" ? item.matchedClaimId : null);
      if (claimId) {
        queryClient.invalidateQueries({ queryKey: [`/api/claims/${claimId}/timeline`] });
        queryClient.invalidateQueries({ queryKey: [`/api/claims/${claimId}/tasks`] });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
    },
  });
  const states = useMutationState({ filters: { mutationKey }, select: (entry) => entry.state as AgentRunState });
  const latest: AgentRunState | undefined = states[states.length - 1];
  return { run: (trigger?: string) => mutation.mutate(trigger), running: latest?.status === "pending", latest };
}

const RUN_STATUS_TEXT: Readonly<Record<string, string>> = {
  completed: "Completed",
  requires_approval: "Needs a person",
  skipped: "Skipped",
  failed: "Failed",
  running: "Running",
  queued: "Queued",
  cancelled: "Cancelled",
};

export const ALREADY_RAN_TEXT = "Already ran (one run per item, per day for firm runs)";

/** A run's status in plain words (agent_runs.status, or skipped). */
export function runStatusText(status: string): string {
  return RUN_STATUS_TEXT[status] ?? status.replace(/_/g, " ");
}

export interface RunOutcome {
  text: string;
  tone: "done" | "attention" | "error";
  runId: string | null;
}

/** The chip after a run: its status, or the server's message when it refused (409) or the model was unavailable (503). */
export function runOutcome(state: AgentRunState | undefined): RunOutcome | null {
  if (!state) return null;
  if (state.status === "success" && state.data) {
    const { status, triage, runId } = state.data;
    if (triage === "already_run" || status === "already_run") return { text: ALREADY_RAN_TEXT, tone: "attention", runId };
    return {
      text: runStatusText(status),
      tone: status === "requires_approval" ? "attention" : status === "failed" ? "error" : "done",
      runId,
    };
  }
  if (state.status === "error" && state.error) {
    const error = state.error;
    if (error instanceof ApiError && error.status === 503) {
      const runId = typeof error.data?.runId === "string" ? error.data.runId : null;
      return { text: `Model unavailable: ${error.message}`, tone: "error", runId };
    }
    return { text: error.message || "The run failed", tone: "error", runId: null };
  }
  return null;
}
